import { KebabIcon } from "assets/icons";
import { Table } from "components/Common/Table/Table";
import BottomSheet from "components/foundation/BottomSheet/BottomSheet";
import Button from "components/foundation/Button";
import CheckBox from "components/foundation/CheckBox/CheckBox";
import Toast from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import dayjs from "dayjs";
import useMediaView from "features/common/hooks/useMediaView";
import React, { MouseEvent, useState } from "react";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { match } from "ts-pattern";
import copyToClipboard from "utils/copyClipboard";
import downloadPDF from "utils/downloadPdf";
import openPdfPreview from "utils/openPdfPreview";

interface WorkTableRowProps {
  id: string;
  no: number;
  title: string;
  pj: string;
  documentType: string;
  author: string;
  createAt: string;
  documentFileUrl?: string;
  isSelected?: boolean;
  onCheckBoxClick?: (id: string, documentFileUrl?: string) => void;
}

const WorkTableRow = ({
  id,
  no,
  title,
  pj,
  documentType,
  author,
  createAt,
  documentFileUrl,
  isSelected = false,
  onCheckBoxClick,
}: WorkTableRowProps) => {
  const isSmall = useMediaView("small");
  const [isEtcOpen, setIsEtcOpen] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpenPdf = (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    e.stopPropagation();
    openPdfPreview(documentFileUrl, title);
  };

  const handleDownloadPdf = (
    e: MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    documentFileUrl && downloadPDF(documentFileUrl, formattedCreateAtDate);
  };

  const handleCopyLink = (
    e: MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    documentFileUrl && copyToClipboard(documentFileUrl);
    setIsToastOpen(true);
  };

  const formattedCreateAtDate = dayjs(createAt).format("YYYY-MM-DD");

  const handleDetailClick = () => {
    navigate(ROUTER_PATH.document.work.detail(id));
  };

  const handlePdfBottomSheetOpenClick = (e: MouseEvent<HTMLOrSVGElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEtcOpen(true);
  };

  const handleCheckBoxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCheckBoxClick?.(id, documentFileUrl);
  };

  return (
    <Table.Tr
      onClick={handleDetailClick}
      style={{
        cursor: "pointer",
      }}
    >
      {match(isSmall)
        .with(true, () => (
          <>
            <Table.Td>{pj}</Table.Td>
            <Table.Td>{title}</Table.Td>
            <Table.Td>{formattedCreateAtDate}</Table.Td>
            <Table.Td
              style={{
                margin: "auto",
              }}
            >
              {documentFileUrl && (
                <KebabIcon onClick={handlePdfBottomSheetOpenClick} />
              )}
              <Toast.Message
                open={isToastOpen}
                onOpenChange={(open) => setIsToastOpen(open)}
              >
                링크가 복사되었습니다.
              </Toast.Message>
            </Table.Td>
            <BottomSheet title="PDF" isOpen={isEtcOpen} setOpen={setIsEtcOpen}>
              <div onClick={handleOpenPdf}>조회</div>
              <div onClick={handleDownloadPdf}>다운로드</div>
              <div onClick={handleCopyLink}>링크복사</div>
            </BottomSheet>
          </>
        ))
        .otherwise(() => (
          <>
            <Table.Td
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CheckBox
                onClick={handleCheckBoxClick}
                checked={isSelected}
                disabled={!documentFileUrl}
              />
            </Table.Td>
            <Table.Td>{no.toString()}</Table.Td>
            <Table.Td>{pj}</Table.Td>
            <Table.Td>{documentType}</Table.Td>
            <Table.Td>{title}</Table.Td>
            <Table.Td>{author}</Table.Td>
            <Table.Td>{formattedCreateAtDate}</Table.Td>
            <Table.Td>
              {documentFileUrl && (
                <ButtonWrapper>
                  <Button variant="primary" onClick={handleOpenPdf}>
                    조회
                  </Button>
                  <Button variant="tertiary" onClick={handleDownloadPdf}>
                    다운로드
                  </Button>
                  <Button variant="tertiary" onClick={handleCopyLink}>
                    링크복사
                  </Button>
                  <Toast.Message
                    open={isToastOpen}
                    onOpenChange={(open) => setIsToastOpen(open)}
                  >
                    링크가 복사되었습니다.
                  </Toast.Message>
                </ButtonWrapper>
              )}
            </Table.Td>
          </>
        ))}
    </Table.Tr>
  );
};

export default WorkTableRow;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`;
